/**
 * Response contract for the `get-queue-metrics` Edge Function (YAR-208).
 *
 * Lives in the web app rather than in the function so the Edge Function and
 * the dashboard cannot drift apart — `admin-metrics` shares its payload type
 * the same way.
 */

/** One pgmq queue as `pgmq.metrics_all()` reports it. */
export interface AdminQueueDepth {
  /** Messages currently in the queue. */
  depth: number;
  /**
   * Age of the oldest / newest message, in seconds. NULL on an empty queue —
   * pgmq has nothing to measure, which is not the same as "0 seconds old".
   */
  oldest_msg_age_sec: number | null;
  newest_msg_age_sec: number | null;
  /** pgmq's lifetime message counter for the queue. */
  total_messages: number;
}

export interface AdminQueuePipeline {
  /**
   * Mean seconds from `emails.created_at` to a SUCCEEDED extraction over the
   * last 24h. NULL when nothing completed in the window.
   */
  avg_processing_sec: number | null;
  completed_last_24h: number;
  /** Emails parked in ERROR. All-time: nothing re-enqueues an ERROR email. */
  error_email_count: number;
  /**
   * ERROR emails that failed inside the last `ERROR_RECENT_WINDOW_DAYS`. The
   * all-time count never goes down, so it cannot say whether anything is
   * failing now; this is the number worth alerting on.
   */
  recent_error_email_count: number;
  /** Emails in HELD, waiting out a transient LLM failure (YAR-212). */
  held_email_count: number;
  /**
   * HELD emails whose `held_until` has passed, plus any with no deadline at
   * all. Both should be zero in steady state: the first means the cleanup
   * sweep is not re-enqueuing, the second can never return on its own.
   */
  held_overdue_count: number;
}

export interface AdminQueueMetrics {
  generated_at: string;
  queues: {
    email_processing: AdminQueueDepth;
    email_retry: AdminQueueDepth;
  };
  pipeline: AdminQueuePipeline;
}

/**
 * Returned with HTTP 200, not 500: a queue-metrics outage must not read as a
 * dashboard outage, and the cron that scrapes this endpoint should not retry.
 */
export interface AdminQueueMetricsUnavailable {
  error: 'pgmq_unavailable';
  message: string;
}

export type AdminQueueMetricsResponse =
  | AdminQueueMetrics
  | AdminQueueMetricsUnavailable;

export function isQueueMetricsUnavailable(
  body: AdminQueueMetricsResponse,
): body is AdminQueueMetricsUnavailable {
  return (body as AdminQueueMetricsUnavailable).error === 'pgmq_unavailable';
}

/** Window, in days, behind `recent_error_email_count`. */
export const ERROR_RECENT_WINDOW_DAYS = 7;

/** Depth at which `email_processing` is considered backed up. */
export const QUEUE_DEPTH_WARN_THRESHOLD = 50;

/**
 * How long `email_processing` must stay above the threshold before the
 * backpressure alert latches. A single 1-minute cron tick can legitimately
 * spike the depth; five continuous minutes cannot.
 */
export const BACKPRESSURE_SUSTAIN_MS = 5 * 60_000;
